/**
 * Design bucket: structure joinery / engineering + proposal-level estimating.
 * Missing optional fields keep the legacy four-hour formula.
 */

import type {
  DesignEstimatingData,
  DesignerInvoiceMarkupRegion,
  DesignerInvoiceSource,
  DesignNodeData,
  EngineeringBillingMode,
  ProposalBuilderStatus,
} from '../types';

const DESIGNER_INVOICE_SOURCES: readonly DesignerInvoiceSource[] = ['manual', 'agent', 'email'];

/** True when Global Costs can arm Space Quick Look for the designer invoice $. */
export function hasDesignerInvoicePreview(estimating: DesignEstimatingData | null | undefined): boolean {
  return !!(estimating?.designerInvoiceFileUrl || estimating?.designerInvoiceStoragePath);
}

export function normalizeDesignerInvoiceMarkupRegions(
  raw: unknown,
): DesignerInvoiceMarkupRegion[] | undefined {
  if (raw === undefined) return undefined;
  if (raw === null) return [];
  if (!Array.isArray(raw)) return undefined;
  const out: DesignerInvoiceMarkupRegion[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const r = item as Record<string, unknown>;
    const pageNumber = Math.max(1, Math.floor(Number(r.pageNumber) || 0));
    const x = Number(r.x);
    const y = Number(r.y);
    const w = Number(r.w);
    const h = Number(r.h);
    if (![pageNumber, x, y, w, h].every((n) => Number.isFinite(n))) continue;
    const region: DesignerInvoiceMarkupRegion = { pageNumber, x, y, w, h };
    if (typeof r.label === 'string' && r.label.trim()) region.label = r.label.trim();
    const amount = Number(r.amount);
    if (Number.isFinite(amount)) region.amount = amount;
    out.push(region);
  }
  return out;
}

export function normalizeDesignerInvoiceSource(raw: unknown): DesignerInvoiceSource | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined;
  const s = String(raw).trim().toLowerCase();
  return (DESIGNER_INVOICE_SOURCES as readonly string[]).includes(s)
    ? (s as DesignerInvoiceSource)
    : undefined;
}

/** Pick invoice PDF / markup fields for lock snapshots and agent merges. */
export function pickDesignerInvoiceDocFields(
  estimating: DesignEstimatingData | null | undefined,
): Partial<DesignEstimatingData> {
  if (!estimating) return {};
  const out: Partial<DesignEstimatingData> = {};
  if (estimating.designerInvoiceFileUrl !== undefined) {
    out.designerInvoiceFileUrl = estimating.designerInvoiceFileUrl;
  }
  if (estimating.designerInvoiceStoragePath !== undefined) {
    out.designerInvoiceStoragePath = estimating.designerInvoiceStoragePath;
  }
  if (estimating.designerInvoiceFileName !== undefined) {
    out.designerInvoiceFileName = estimating.designerInvoiceFileName;
  }
  if (estimating.designerInvoicePage !== undefined) {
    out.designerInvoicePage = estimating.designerInvoicePage;
  }
  if (estimating.designerInvoiceMarkupRegions !== undefined) {
    out.designerInvoiceMarkupRegions = estimating.designerInvoiceMarkupRegions;
  }
  if (estimating.designerInvoiceSource !== undefined) {
    out.designerInvoiceSource = estimating.designerInvoiceSource;
  }
  if (estimating.designerInvoiceNote !== undefined) {
    out.designerInvoiceNote = estimating.designerInvoiceNote;
  }
  return out;
}

export type DesignRateInputs = {
  pmRate: number;
  apmRate?: number;
  leadDesignerRate: number;
  supportDesignerRate: number;
  engineeringRate: number;
};

/** Defaults used only when rates are not loaded (send lock / agent fallback). */
export const FALLBACK_DESIGN_RATES: DesignRateInputs = {
  pmRate: 50,
  apmRate: 40,
  leadDesignerRate: 120,
  supportDesignerRate: 40,
  engineeringRate: 120,
};

export const DESIGN_ESTIMATING_LOCK_STATUSES: readonly string[] = [
  'sent',
  'opened',
  'approved',
];

export type DesignAuditLine = {
  key: string;
  label: string;
  detail: string;
  amount: number;
  group: 'estimating' | 'joinery' | 'engineering' | 'legacy';
};

function numOr0(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function finiteOrUndefined(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export function resolveApmRate(rates: DesignRateInputs): number {
  const override = finiteOrUndefined(rates.apmRate);
  return override != null ? override : FALLBACK_DESIGN_RATES.apmRate || 40;
}

/** Per-structure joinery $/hr when set, otherwise proposal defaults. */
export function resolveJoineryRates(
  design: Pick<DesignNodeData, 'pmRate' | 'apmRate' | 'leadDesignerRate' | 'supportDesignerRate'> | null | undefined,
  rates: DesignRateInputs,
): DesignRateInputs {
  return {
    pmRate: finiteOrUndefined(design?.pmRate) ?? numOr0(rates.pmRate),
    apmRate: finiteOrUndefined(design?.apmRate) ?? rates.apmRate,
    leadDesignerRate: finiteOrUndefined(design?.leadDesignerRate) ?? numOr0(rates.leadDesignerRate),
    supportDesignerRate: finiteOrUndefined(design?.supportDesignerRate) ?? numOr0(rates.supportDesignerRate),
    engineeringRate: rates.engineeringRate,
  };
}

export function resolveCrmHourlyRate(
  estimating: DesignEstimatingData | null | undefined,
  rates: DesignRateInputs,
): number {
  const override = finiteOrUndefined(estimating?.crmHourlyRate);
  return override != null ? override : numOr0(rates.pmRate);
}

export function resolveDesignerHourlyRate(
  estimating: DesignEstimatingData | null | undefined,
  rates: DesignRateInputs,
): number {
  const override = finiteOrUndefined(estimating?.designerHourlyRate);
  return override != null ? override : numOr0(rates.leadDesignerRate);
}

export function engineeringBillingMode(
  design: Pick<DesignNodeData, 'engineeringMode'> | null | undefined,
): EngineeringBillingMode {
  return design?.engineeringMode === 'lumpSum' ? 'lumpSum' : 'hourly';
}

export function engineeringRaw(
  design: Pick<DesignNodeData, 'engineeringHours' | 'engineeringMode' | 'engineeringLumpSum'>,
  rates: DesignRateInputs,
): number {
  if (engineeringBillingMode(design) === 'lumpSum') {
    return Math.max(0, numOr0(design.engineeringLumpSum));
  }
  return Math.max(0, numOr0(design.engineeringHours) * numOr0(rates.engineeringRate));
}

export function joineryRaw(
  design: Pick<DesignNodeData, 'pmHours' | 'apmHours' | 'leadDesignerHours' | 'supportDesignerHours' | 'pmRate' | 'apmRate' | 'leadDesignerRate' | 'supportDesignerRate'>,
  rates: DesignRateInputs,
): number {
  const resolved = resolveJoineryRates(design, rates);
  return (
    numOr0(design.pmHours) * numOr0(resolved.pmRate) +
    numOr0(design.apmHours) * resolveApmRate(resolved) +
    numOr0(design.leadDesignerHours) * numOr0(resolved.leadDesignerRate) +
    numOr0(design.supportDesignerHours) * numOr0(resolved.supportDesignerRate)
  );
}

export function legacyPmRaw(
  design: Pick<DesignNodeData, 'pmHours'>,
  rates: DesignRateInputs,
): number {
  return numOr0(design.pmHours) * numOr0(rates.pmRate);
}

/** Structure-only Design raw. Same as today's four-term formula when APM / lump fields are absent. */
export function structureDesignRaw(
  design: DesignNodeData | null | undefined,
  rates: DesignRateInputs,
): number {
  if (!design) return 0;
  return joineryRaw(design, rates) + engineeringRaw(design, rates);
}

export function stampStructureDesignTotal(
  design: DesignNodeData,
  rates: DesignRateInputs,
): DesignNodeData {
  return { ...design, total: structureDesignRaw(design, rates) };
}

export function isSentLikeStatus(status?: string | null): boolean {
  return !!status && DESIGN_ESTIMATING_LOCK_STATUSES.includes(status);
}

/**
 * Estimating is locked after send unless staff explicitly unlocks.
 * Already-sent proposals without a lock flag are treated as locked.
 */
export function isDesignEstimatingLocked(
  estimating: DesignEstimatingData | null | undefined,
  status?: string | null,
): boolean {
  if (estimating?.unlocked === true) return false;
  if (estimating?.locked === true) return true;
  return isSentLikeStatus(status);
}

export function estimatingLiveRaw(
  estimating: DesignEstimatingData | null | undefined,
  rates: DesignRateInputs,
): number {
  if (!estimating) return 0;
  const crm = numOr0(estimating.crmHours) * resolveCrmHourlyRate(estimating, rates);
  const designer = numOr0(estimating.designerHours) * resolveDesignerHourlyRate(estimating, rates);
  return Math.max(0, crm + designer + numOr0(estimating.designerInvoiceAmount));
}

/**
 * Proposal-level estimating raw. Missing block = $0 (old proposals unchanged).
 * When locked, prefer the snapshot so later hour writes cannot grow the quote.
 */
export function estimatingRaw(
  estimating: DesignEstimatingData | null | undefined,
  rates?: DesignRateInputs | null,
  status?: string | null,
): number {
  if (!estimating) return 0;
  const locked = isDesignEstimatingLocked(estimating, status);
  if (locked) {
    const snap = finiteOrUndefined(estimating.lockedTotal);
    if (snap != null) return Math.max(0, snap);
  }
  const resolvedRates = rates || FALLBACK_DESIGN_RATES;
  if (rates || hasEstimatingInputs(estimating)) {
    return estimatingLiveRaw(estimating, resolvedRates);
  }
  const stored = finiteOrUndefined(estimating.total);
  return stored != null ? Math.max(0, stored) : 0;
}

/** Dollar-line global costs plus project-level estimating. Same OH% base as before. */
export function effectiveGlobalCostsTotal(
  globalLineItems: number | null | undefined,
  estimating?: DesignEstimatingData | null,
  rates?: DesignRateInputs | null,
  status?: string | null,
): number {
  return Math.max(0, numOr0(globalLineItems)) + estimatingRaw(estimating, rates, status);
}

function hasEstimatingInputs(estimating: DesignEstimatingData): boolean {
  return (
    numOr0(estimating.crmHours) !== 0 ||
    numOr0(estimating.designerHours) !== 0 ||
    numOr0(estimating.designerInvoiceAmount) !== 0
  );
}

export function stampDesignEstimatingTotal(
  estimating: DesignEstimatingData | null | undefined,
  rates: DesignRateInputs,
  status?: string | null,
): DesignEstimatingData {
  const base: DesignEstimatingData = { ...(estimating || {}) };
  const live = estimatingLiveRaw(base, rates);
  if (isDesignEstimatingLocked(base, status) && finiteOrUndefined(base.lockedTotal) != null) {
    return { ...base, total: live };
  }
  return { ...base, total: live };
}

export function lockDesignEstimating(
  estimating: DesignEstimatingData | null | undefined,
  rates?: DesignRateInputs | null,
  lockedAt: Date = new Date(),
): DesignEstimatingData {
  const resolved = rates || FALLBACK_DESIGN_RATES;
  const live = estimatingLiveRaw(estimating, resolved);
  const crmHourlyRate = finiteOrUndefined(estimating?.crmHourlyRate);
  const designerHourlyRate = finiteOrUndefined(estimating?.designerHourlyRate);
  return {
    crmHours: numOr0(estimating?.crmHours),
    designerHours: numOr0(estimating?.designerHours),
    designerInvoiceAmount: numOr0(estimating?.designerInvoiceAmount),
    ...(crmHourlyRate != null ? { crmHourlyRate } : {}),
    ...(designerHourlyRate != null ? { designerHourlyRate } : {}),
    ...pickDesignerInvoiceDocFields(estimating),
    locked: true,
    unlocked: false,
    lockedAt,
    lockedCrmHours: numOr0(estimating?.crmHours),
    lockedDesignerHours: numOr0(estimating?.designerHours),
    lockedDesignerInvoiceAmount: numOr0(estimating?.designerInvoiceAmount),
    lockedCrmHourlyRate: resolveCrmHourlyRate(estimating, resolved),
    lockedDesignerHourlyRate: resolveDesignerHourlyRate(estimating, resolved),
    lockedTotal: live,
    total: live,
  };
}

export function unlockDesignEstimating(
  estimating: DesignEstimatingData | null | undefined,
): DesignEstimatingData {
  return {
    ...(estimating || {}),
    unlocked: true,
  };
}

export function structureDesignHours(
  design: Pick<
    DesignNodeData,
    'pmHours' | 'apmHours' | 'leadDesignerHours' | 'supportDesignerHours' | 'engineeringHours' | 'engineeringMode'
  >,
): number {
  const joinery =
    numOr0(design.leadDesignerHours) +
    numOr0(design.supportDesignerHours) +
    numOr0(design.pmHours) +
    numOr0(design.apmHours);
  if (engineeringBillingMode(design) === 'lumpSum') return joinery;
  return joinery + numOr0(design.engineeringHours);
}

export function estimatingHours(estimating: DesignEstimatingData | null | undefined): number {
  if (!estimating) return 0;
  if (isDesignEstimatingLocked(estimating) && estimating.locked) {
    return numOr0(estimating.lockedCrmHours ?? estimating.crmHours) +
      numOr0(estimating.lockedDesignerHours ?? estimating.designerHours);
  }
  return numOr0(estimating.crmHours) + numOr0(estimating.designerHours);
}

export function listEstimatingAuditLines(
  estimating: DesignEstimatingData | null | undefined,
  rates: DesignRateInputs,
  status?: string | null,
): DesignAuditLine[] {
  if (!estimating) return [];
  const locked = isDesignEstimatingLocked(estimating, status);
  const crmHours = locked
    ? numOr0(estimating.lockedCrmHours ?? estimating.crmHours)
    : numOr0(estimating.crmHours);
  const designerHours = locked
    ? numOr0(estimating.lockedDesignerHours ?? estimating.designerHours)
    : numOr0(estimating.designerHours);
  const invoice = locked
    ? numOr0(estimating.lockedDesignerInvoiceAmount ?? estimating.designerInvoiceAmount)
    : numOr0(estimating.designerInvoiceAmount);
  const crmRate = locked
    ? numOr0(estimating.lockedCrmHourlyRate ?? resolveCrmHourlyRate(estimating, rates))
    : resolveCrmHourlyRate(estimating, rates);
  const designerRate = locked
    ? numOr0(estimating.lockedDesignerHourlyRate ?? resolveDesignerHourlyRate(estimating, rates))
    : resolveDesignerHourlyRate(estimating, rates);

  const lines: DesignAuditLine[] = [];
  if (crmHours > 0) {
    lines.push({
      key: 'estimating-crm',
      label: 'CRM time',
      detail: `${crmHours}h × $${crmRate}/hr`,
      amount: crmHours * crmRate,
      group: 'estimating',
    });
  }
  if (designerHours > 0) {
    lines.push({
      key: 'estimating-designer',
      label: 'Designer (invoiced separately)',
      detail: `${designerHours}h × $${designerRate}/hr`,
      amount: designerHours * designerRate,
      group: 'estimating',
    });
  }
  if (invoice > 0) {
    const fileHint = estimating.designerInvoiceFileName || estimating.designerInvoiceFileUrl
      ? ' · PDF (hover $ · Space)'
      : '';
    lines.push({
      key: 'estimating-invoice',
      label: 'Designer invoice',
      detail: `Lump invoice${fileHint}`,
      amount: invoice,
      group: 'estimating',
    });
  }
  return lines;
}

export function listStructureDesignAuditLines(
  design: DesignNodeData,
  rates: DesignRateInputs,
): DesignAuditLine[] {
  const resolved = resolveJoineryRates(design, rates);
  const lines: DesignAuditLine[] = [];
  if (numOr0(design.pmHours) > 0) {
    lines.push({
      key: 'joinery-pm',
      label: 'PM',
      detail: `${design.pmHours}h × $${resolved.pmRate}/hr`,
      amount: legacyPmRaw(design, resolved),
      group: 'joinery',
    });
  }
  if (numOr0(design.apmHours) > 0) {
    const apmRate = resolveApmRate(resolved);
    lines.push({
      key: 'joinery-apm',
      label: 'APM',
      detail: `${design.apmHours}h × $${apmRate}/hr`,
      amount: numOr0(design.apmHours) * apmRate,
      group: 'joinery',
    });
  }
  if (numOr0(design.leadDesignerHours) > 0) {
    lines.push({
      key: 'joinery-lead',
      label: 'Lead designer',
      detail: `${design.leadDesignerHours}h × $${resolved.leadDesignerRate}/hr`,
      amount: numOr0(design.leadDesignerHours) * numOr0(resolved.leadDesignerRate),
      group: 'joinery',
    });
  }
  if (numOr0(design.supportDesignerHours) > 0) {
    lines.push({
      key: 'joinery-support',
      label: 'Support designer',
      detail: `${design.supportDesignerHours}h × $${resolved.supportDesignerRate}/hr`,
      amount: numOr0(design.supportDesignerHours) * numOr0(resolved.supportDesignerRate),
      group: 'joinery',
    });
  }
  if (engineeringBillingMode(design) === 'lumpSum') {
    const lump = numOr0(design.engineeringLumpSum);
    if (lump > 0) {
      lines.push({
        key: 'eng-lump',
        label: 'Engineering',
        detail: 'Lump sum',
        amount: lump,
        group: 'engineering',
      });
    }
  } else if (numOr0(design.engineeringHours) > 0) {
    lines.push({
      key: 'eng-hourly',
      label: 'Engineering',
      detail: `${design.engineeringHours}h × $${rates.engineeringRate}/hr`,
      amount: numOr0(design.engineeringHours) * numOr0(rates.engineeringRate),
      group: 'engineering',
    });
  }
  return lines;
}

export function proposalDesignTotal(
  nodeSets: Array<{ design: DesignNodeData; quantity?: number; enabled?: boolean }>,
  estimating: DesignEstimatingData | null | undefined,
  rates: DesignRateInputs,
  status?: string | null,
): number {
  let structure = 0;
  nodeSets.forEach(ns => {
    if (ns.enabled === false) return;
    structure += structureDesignRaw(ns.design, rates) * (ns.quantity || 1);
  });
  return structure;
}

export type ProposalStatusForEstimating = ProposalBuilderStatus | string | null | undefined;
